import { PKCE_COOKIE_NAME } from "./constants";
import { getFirmConfig } from "../utils/firm";

function generateRandom(length: number): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => chars[b % chars.length]).join("");
}

async function sha256Base64Url(plain: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export interface OktaSocialSession {
  codeVerifier: string;
  provider: "google" | "apple";
  redirectUri: string;
}

const SESSION_KEY_PREFIX = `${PKCE_COOKIE_NAME}_`;

export async function initiateOktaSocialLogin(
  provider: "google" | "apple",
  idpId: string,
  redirectUri: string,
): Promise<void> {
  const codeVerifier = generateRandom(64);
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const state = generateRandom(32);

  const session: OktaSocialSession = { codeVerifier, provider, redirectUri };
  sessionStorage.setItem(
    `${SESSION_KEY_PREFIX}${state}`,
    JSON.stringify(session),
  );

  const domain = window.location.hostname;
  const oktaIssuer =
    getFirmConfig(domain, "okta_issuer", process.env.NEXT_PUBLIC_OKTA_ISSUER) ??
    "";
  const oktaClientId =
    getFirmConfig(
      domain,
      "okta_client_id",
      process.env.NEXT_PUBLIC_OKTA_CLIENT_ID,
    ) ?? "";

  const url = new URL(`${oktaIssuer}/v1/authorize`);
  url.searchParams.set("client_id", oktaClientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid profile email");
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  if (provider === "google") {
    // Force chooser flow for users with an existing Google/Okta session.
    url.searchParams.set("prompt", "login select_account");
    url.searchParams.set("max_age", "0");
  } else if (provider === "apple") {
    // Force account selector for users with an existing Apple/Okta session.
    url.searchParams.set("prompt", "login select_account");
  }
  if (idpId) url.searchParams.set("idp", idpId);

  window.location.href = url.toString();
}

export interface OktaTokens {
  idToken: string;
  accessToken: string;
  provider: "google" | "apple";
}

export async function exchangeOktaCode(
  code: string,
  state: string,
): Promise<OktaTokens> {
  const raw = sessionStorage.getItem(`${SESSION_KEY_PREFIX}${state}`);
  if (!raw) throw new Error("PKCE session not found. Please try again.");

  const session: OktaSocialSession = JSON.parse(raw);
  sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${state}`);

  const domain = window.location.hostname;
  const oktaIssuer =
    getFirmConfig(domain, "okta_issuer", process.env.NEXT_PUBLIC_OKTA_ISSUER) ??
    "";
  const oktaClientId =
    getFirmConfig(
      domain,
      "okta_client_id",
      process.env.NEXT_PUBLIC_OKTA_CLIENT_ID,
    ) ?? "";

  const resp = await fetch(`${oktaIssuer}/v1/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: oktaClientId,
      redirect_uri: session.redirectUri,
      code,
      code_verifier: session.codeVerifier,
    }),
  });

  const data = await resp.json();
  if (!resp.ok || !data.id_token) {
    throw new Error(data.error_description ?? "Token exchange failed.");
  }

  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    provider: session.provider,
  };
}
